import { useState, useRef, useEffect } from "react";
import { Icon } from "@iconify/react";
import type { HealthRecord } from "@/shared/types";
import { Button } from "@/shared/components/button";
import { Textarea } from "@/shared/components/dialog";
import { cn } from "@/shared/lib/utils";

interface AskRecordsSectionProps {
  records?: HealthRecord[];
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

const quickQuestions = [
  { icon: "lucide:flask-conical", category: "Blood test",  text: "What does my recent blood test mean?"     },
  { icon: "lucide:pill",          category: "Medications", text: "How should I take my medications?"        },
  { icon: "lucide:flask-conical", category: "Blood test",  text: "What does my recent blood test mean?"     },
  { icon: "lucide:alert-circle",  category: "Side Effects",text: "What are the side effects of ACE inhibitors?" },
];

const DUMMY_RESPONSE = `Based on your Complete Blood Count (CBC) from March 15, 2026, here's what your results mean:
Overall Summary: Your blood test came back mostly normal! Here are the key findings:

**1. White Blood Cells (11.2 K/uL) – Slightly elevated**

- This is just above the normal range of 4.5–11.0
- This could indicate your body was recently fighting off a minor infection, like a cold
- Unless you're experiencing symptoms, this is usually nothing to worry about

**2. Red Blood Cells, Hemoglobin, and Platelets – All normal**

- These indicate good oxygen delivery throughout your body
- Your blood clotting ability is healthy

My recommendation: Mention the slightly elevated white blood cells at your next doctor visit. If you were feeling under the weather around the time of the test, that likely explains it!`;

// ── Render AI message content with bold headings and bullet points ──────────
function RenderContent({ content }: { content: string }) {
  const lines = content.split("\n");
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-2" />;
        // Bold line **text**
        if (line.startsWith("**") && line.endsWith("**")) {
          const text = line.slice(2, -2);
          return (
            <p key={i} className="text-sm font-extrabold text-[#0A2947]">{text}</p>
          );
        }
        // Bullet
        if (line.startsWith("- ")) {
          return (
            <p key={i} className="text-sm text-[#4B5563]">{line}</p>
          );
        }
        return <p key={i} className="text-sm text-[#4B5563]">{line}</p>;
      })}
    </div>
  );
}

export function AskRecordsSection({ records = [] }: AskRecordsSectionProps) {
  const [messages, setMessages]   = useState<Message[]>([]);
  const [input, setInput]         = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const bottomRef                 = useRef<HTMLDivElement>(null);
  const hasMessages               = messages.length > 0;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || isLoading) return;
    const userMsg: Message = {
      id: Date.now().toString(), role: "user",
      content: text.trim(), timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);

    // Simulate AI response
    await new Promise(r => setTimeout(r, 1200));
    const aiMsg: Message = {
      id: (Date.now() + 1).toString(), role: "assistant",
      content: DUMMY_RESPONSE, timestamp: new Date(),
    };
    setMessages(prev => [...prev, aiMsg]);
    setIsLoading(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  // ── Input box (shared between empty + chat states) ────────────────────
  const inputBox = (
    <div className="rounded-3xl border border-[#E5E7EB] bg-white px-5 pt-5 pb-4 shadow-[0_4px_14px_rgba(0,0,0,0.12)]">
      <div className="flex items-start gap-3">
        {/* Circular chat icon */}
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
          <Icon icon="lucide:refresh-cw" className="h-4 w-4 text-[#9CA3AF]" />
        </div>
        <Textarea
          variant="borderless"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="What can I do for you today?"
          rows={4}
          autoSize={false}
          className="!min-h-[90px] !flex-1 !resize-none !p-0 !text-sm !font-medium !text-[#0A2947] placeholder:!text-[#9CA3AF]"
        />
      </div>
      {/* Bottom row */}
      <div className="mt-3 flex items-center justify-end gap-2">
        {/* MedSyncIQ pill */}
        <Button
          className="!flex !h-auto !items-center !gap-1.5 !rounded-full !bg-[#0A2947] !px-3 !py-1.5 !text-xs !font-bold !text-white hover:!bg-[#0A2947]"
        >
          MedSyncIQ.AI
        </Button>
        <Button
          variant="outline"
          onClick={() => sendMessage(input)}
          disabled={!input.trim() || isLoading}
          className={cn(
            "!flex !h-8 !w-8 !items-center !justify-center !rounded-full !p-0",
            input.trim()
              ? "!border-[#0A2947] !bg-[#0A2947] !text-white hover:!border-[#0A2947] hover:!bg-[#0A2947] hover:!text-white"
              : "!border-[#E5E7EB] !text-[#4B5563] hover:!bg-[#F3F4F6] hover:!text-[#4B5563]"
          )}
        >
          <Icon icon="lucide:send" className={cn("h-4 w-4", input.trim() && "text-white")} />
        </Button>
      </div>
    </div>
  );

  // ── Quick question chips ──────────────────────────────────────────────
  const QuickChips = ({ compact = false }: { compact?: boolean }) => (
    <div className={cn("flex flex-wrap gap-3", compact ? "" : "justify-center")}>
      {quickQuestions.map((q, i) => (
        <Button
          key={i}
          variant="outline"
          onClick={() => sendMessage(q.text)}
          className={cn(
            "!flex !h-auto !min-h-[58px] !w-full !items-start !gap-2 !whitespace-normal !rounded-xl !border-[#F7B4C3] !bg-white !px-3 !py-2 !text-left hover:!border-[#F36F8F] hover:!bg-white hover:!shadow-sm",
            compact ? "!max-w-[160px]" : "!max-w-[145px]"
          )}
        >
          <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F17893]">
            <Icon icon={q.icon} className="h-3 w-3 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-extrabold text-[#0A2947]">{q.category}</p>
            <p className="break-words text-[10px] font-semibold leading-tight text-[#0A2947]">{q.text}</p>
          </div>
        </Button>
      ))}
    </div>
  );

  // ── Empty / welcome state ─────────────────────────────────────────────
  if (!hasMessages) {
    return (
      <div className="flex min-h-full items-center justify-center bg-[#F7F7F7] px-6 py-10">
        <div className="relative w-full max-w-[760px]">
        {/* Doctor illustration top-right */}
        <div className="hidden">
          👨‍⚕️
        </div>

        {/* Heading */}
        <div className="mb-14 flex items-end justify-between gap-8">
          <div>
            <h1 className="text-4xl font-medium leading-tight tracking-tight text-[#111827]">
              Hello, I am <span className="font-extrabold text-[#F17893]">MedSyncIQ.AI</span>
          </h1>
            <p className="mt-2 text-3xl font-normal leading-tight text-[#3A3D42]">
              Do you need my guidance?
            </p>
          </div>
          <div className="relative hidden h-44 w-36 shrink-0 md:block">
            <div className="absolute left-1/2 top-0 h-16 w-14 -translate-x-1/2 rounded-[45%] bg-[#F2C4A8]" />
            <div className="absolute left-[42px] top-0 h-9 w-14 rounded-t-full bg-[#111827]" />
            <div className="absolute left-[54px] top-[60px] h-7 w-8 rounded-b-xl bg-[#F2C4A8]" />
            <div className="absolute left-4 top-20 h-24 w-28 rounded-t-[34px] bg-white shadow-sm" />
            <div className="absolute left-0 top-24 h-20 w-12 rotate-[-18deg] rounded-2xl bg-white shadow-sm" />
            <div className="absolute right-0 top-24 h-20 w-12 rotate-[18deg] rounded-2xl bg-white shadow-sm" />
            <div className="absolute left-[62px] top-20 h-20 w-3 bg-[#111827]" />
            <div className="absolute left-[48px] top-20 h-20 w-4 rotate-[14deg] bg-[#DDE3EA]" />
            <div className="absolute left-[76px] top-20 h-20 w-4 rotate-[-14deg] bg-[#DDE3EA]" />
            <div className="absolute left-[30px] top-[118px] h-10 w-20 rounded-md bg-[#9CA3AF]" />
            <div className="absolute left-[36px] top-[124px] h-1 w-8 rounded-full bg-[#D1D5DB]" />
            <div className="absolute left-[36px] top-[132px] h-1 w-12 rounded-full bg-[#D1D5DB]" />
          </div>
        </div>

        {/* Input box */}
        {inputBox}

        {/* Quick questions */}
        <div className="mt-5">
          <p className="mb-4 text-sm font-extrabold text-[#0A2947]">Quick questions:</p>
          <QuickChips />
        </div>
        </div>
      </div>
    );
  }

  // ── Chat / conversation state ─────────────────────────────────────────
  return (
    <div className="flex h-full flex-col bg-[#F5F5F5]">

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="mx-auto max-w-3xl space-y-6">
          {messages.map((msg) => (
            <div key={msg.id}>
              {msg.role === "user" ? (
                /* User bubble — pink pill right-aligned */
                <div className="flex justify-end">
                  <div className="max-w-sm rounded-2xl rounded-tr-sm bg-[#F4A7B9] px-4 py-2.5">
                    <p className="text-sm font-medium text-[#0A2947]">{msg.content}</p>
                  </div>
                </div>
              ) : (
                /* AI response — left-aligned plain text */
                <div className="max-w-2xl">
                  <RenderContent content={msg.content} />
                  {/* Action buttons */}
                  <div className="mt-3 flex items-center gap-3">
                    <Button variant="link" className="!h-auto !p-0 !text-[#9CA3AF] hover:!text-[#4B5563]">
                      <Icon icon="lucide:copy" className="h-4 w-4" />
                    </Button>
                    <Button variant="link" className="!h-auto !p-0 !text-[#9CA3AF] hover:!text-[#4B5563]">
                      <Icon icon="lucide:thumbs-up" className="h-4 w-4" />
                    </Button>
                    <Button variant="link" className="!h-auto !p-0 !text-[#9CA3AF] hover:!text-[#4B5563]">
                      <Icon icon="lucide:thumbs-down" className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {[0,1,2].map(i => (
                  <div
                    key={i}
                    className="h-2 w-2 rounded-full bg-[#E4002B] animate-bounce"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
              <p className="text-xs text-[#9CA3AF]">MedSyncIQ is thinking...</p>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </div>

      {/* Fixed bottom — quick chips + input */}
      <div className="shrink-0 border-t border-[#E5E7EB] bg-[#F5F5F5] px-6 py-4">
        <div className="mx-auto max-w-3xl">
          {/* Quick chips float above input */}
          <div className="mb-3 flex flex-wrap gap-2">
            {quickQuestions.map((q, i) => (
              <Button
                key={i}
                variant="outline"
                onClick={() => sendMessage(q.text)}
                className="!h-auto !max-w-full !whitespace-normal !break-words !rounded-xl !border-[#E5E7EB] !bg-white !px-3 !py-1.5 !text-left !text-xs !font-medium !text-[#0A2947] hover:!border-[#E4002B]/30 hover:!bg-white hover:!shadow-sm"
              >
                {q.text}
              </Button>
            ))}
          </div>
          {inputBox}
        </div>
      </div>
    </div>
  );
}

export const AskQuestionView = AskRecordsSection;
export const AskQuestion = AskRecordsSection;
